/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/config/enums.ts
 * PURPOSE: Centralized enum values for API responses (DRY principle)
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * DATE: 2026-01-15
 * COMPLIANCE: SDLC 6.3.1 + ADR-api-enum-value-convention.md
 *
 * @business-context
 * - Domain: Vietnamese F&B, Multi-Industry BPM
 * - Impact: System-wide consistency for API enum values
 * - Stakeholders: Frontend Team, Backend Team, QA
 *
 * @technical-architecture
 * - Layer: Configuration
 * - Pattern: Global Constants, DRY Principle
 * - Dependencies: None (leaf module)
 *
 * @cross-references
 * - Design: docs/02-design/GLOBAL-CONSTANTS-PATTERN.md
 * - ADR: docs/02-design/ADR-api-enum-value-convention.md
 * - Backend sync: backend/app/core/constants.py
 */

// ============================================================================
// ATTENDANCE STATUS
// ============================================================================

export const AttendanceStatus = {
  NOT_CLOCKED_IN: "not_clocked_in",
  CLOCKED_IN: "clocked_in",
  CLOCKED_OUT: "clocked_out",
  BREAK_START: "break_start",
  BREAK_END: "break_end",
  ABSENT: "absent",
} as const;

export type AttendanceStatusType = typeof AttendanceStatus[keyof typeof AttendanceStatus];

// ============================================================================
// SHIFT STATUS
// ============================================================================

export const ShiftStatus = {
  SCHEDULED: "scheduled",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
} as const;

export type ShiftStatusType = typeof ShiftStatus[keyof typeof ShiftStatus];

// ============================================================================
// SCHEDULE STATUS
// ============================================================================

export const ScheduleStatus = {
  DRAFT: "draft",
  PUBLISHED: "published",
  ARCHIVED: "archived",
} as const;

export type ScheduleStatusType = typeof ScheduleStatus[keyof typeof ScheduleStatus];

// ============================================================================
// USER STATUS
// ============================================================================

export const UserStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  PENDING: "pending",
  SUSPENDED: "suspended",
} as const;

export type UserStatusType = typeof UserStatus[keyof typeof UserStatus];

// ============================================================================
// GPS CONFIGURATION
// Configurable via environment variables for different deployment environments
// Reference: INFRASTRUCTURE-STANDARD-CONFIGURATION.md
// ============================================================================

export const GPSConfig = {
  // Default GPS accuracy in meters (configurable via VITE_GPS_ACCURACY_THRESHOLD)
  // Per gps-attendance-api.yaml §BranchLocation: default max_clock_in_distance_meters = 100m. Per-branch config overrides at runtime.
  // Per-branch threshold from backend overrides this value at runtime
  DEFAULT_ACCURACY: parseFloat(import.meta.env.VITE_GPS_ACCURACY_THRESHOLD || '100'),

  // GPS timeout in milliseconds (configurable via VITE_GPS_TIMEOUT)
  TIMEOUT: parseInt(import.meta.env.VITE_GPS_TIMEOUT || '30000'),

  // Enable high accuracy mode (configurable via VITE_GPS_HIGH_ACCURACY)
  HIGH_ACCURACY: import.meta.env.VITE_GPS_HIGH_ACCURACY !== 'false',
} as const;

// ============================================================================
// API CONFIGURATION
// Configurable via environment variables for different deployment environments
// ============================================================================

export const APIConfig = {
  // Default API timeout in milliseconds (configurable via VITE_API_TIMEOUT)
  DEFAULT_TIMEOUT: parseInt(import.meta.env.VITE_API_TIMEOUT || '10000'),

  // Long-running operation timeout (exports, reports) (configurable via VITE_API_LONG_TIMEOUT)
  LONG_TIMEOUT: parseInt(import.meta.env.VITE_API_LONG_TIMEOUT || '30000'),

  // Retry attempts for failed requests (configurable via VITE_API_RETRY_ATTEMPTS)
  RETRY_ATTEMPTS: parseInt(import.meta.env.VITE_API_RETRY_ATTEMPTS || '3'),
} as const;

// ============================================================================
// TENANT CONFIGURATION
// Multi-tenant system - configurable via environment variables
// ============================================================================

export const TenantConfig = {
  // Sprint 52 (S52-006): DEPRECATED — Use useAuthStore().tenantKey instead (CTO G1).
  // Kept for backward compatibility during transition. Will be removed in Phase 5.
  // Pre-login fallback only (env-based for initial page load before auth).
  DEFAULT_TENANT_KEY: import.meta.env.VITE_TENANT_KEY || 'nqh-holdings',
} as const;

// ============================================================================
// API ENDPOINTS
// Centralized endpoint paths - DO NOT include /api/v1 prefix
// The apiRequest function already adds API_VERSION prefix
// Reference: docs/03-integrate/01-API-Specifications/
// ============================================================================

// API Version prefix - used by apiRequest function and buildApiUrl helper
export const API_VERSION = '/api/v1';

export const APIEndpoints = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    ME: '/auth/me',
    REFRESH: '/auth/refresh',
  },

  // Attendance
  ATTENDANCE: {
    STATUS: '/attendance/status',
    CLOCK_IN: '/attendance/clock-in',
    CLOCK_OUT: '/attendance/clock-out',
    CLOCK: '/attendance/clock',
    HISTORY: '/attendance/history',
    TEAM: '/attendance/team',
    EVIDENCE: (attendanceId: string) => `/attendance/evidence/${attendanceId}`,
  },

  // Schedules
  SCHEDULES: {
    LIST: '/schedules',
    MY_SCHEDULE: '/schedules/my-schedule',
    SWAP: '/schedules/swap',
  },

  // Organizations
  ORGANIZATIONS: {
    LIST: '/organizations',
  },

  // Audit
  AUDIT: {
    EVENTS: '/audit/events',
  },
} as const;

/**
 * Build full API URL with version prefix
 * Use this when calling fetch directly (not apiRequest)
 * @param endpoint - Endpoint path from APIEndpoints (without /api/v1)
 * @returns Full URL path with /api/v1 prefix
 */
export function buildApiUrl(endpoint: string): string {
  return `${API_VERSION}${endpoint}`;
}
